import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { UiComponentsModule, IconsPreloadService } from '@fcom/ui-components';
import { UiTranslateModule } from '@fcom/ui-translate';
import { ComponentsModule } from '@fcom/common/components/components.module';
import { DirectiveModule } from '@fcom/common/directives';
import { GtmModule } from '@fcom/common/gtm/gtm.module';
import { PipesModule } from '@fcom/common/pipes/pipes.module';
import { LoginModule } from '@fcom/common/login/login.module';
import { NavigationModule } from '@fcom/common/components/header/navigation.module';
import { BookingCommonModule } from '@fcom/booking/modules/common/booking-common.module';
import { LocationsModule } from '@fcom/locations';
import { ImagesModule } from '@fcom/common/components/images';
import { QualtricsModule } from '@fcom/common/qualtrics/qualtrics.module';
import { CmsModule } from '@fcom/cms';
import { CmsPageService, FragmentService } from '@fcom/common';
import { CommonBookingComponentsModule } from '@fcom/common-booking/common-booking.module';

import { PnrWidgetModule } from '../pnr-widget/pnr-widget.module';
import { OneUpgradeRouteModule } from './one-upgrade.routes';
import { ApiModule } from './api/api.module';
import { reducers } from './store';
import { StatusEffects } from './store/effects/status.effects';
import { UpgradeFlightsEffects } from './store/effects/upgrade-flights.effects';
import { AuthEffects } from './store/effects/auth.effects';
import { OneUpgradeLayoutComponent } from './views/layout/layout.component';
import { LoginComponent } from './views/login/login.component';
import { ReviewComponent } from './views/review/review.component';
import { ErrorComponent } from './views/error/error.component';
import { SuccessComponent } from './views/success/success.component';
import { ProcessingComponent } from './views/processing/processing.component';
import { BoundCardComponent } from './components/bound-card/bound-card.component';
import { JourneyInfoComponent } from './components/journey-info/journey-info.component';
import { ReviewBoundsComponent } from './components/review-bounds/review-bounds.component';
import { UpgradeBoundsComponent } from './components/upgrade-bounds/upgrade-bounds.component';
import { UpgradeFlightsComponent } from './components/upgrade-flights/upgrade-flights.component';
import { PassengerSelectionComponent } from './components/passenger-selection/passenger-selection.component';
import {
  LoginGuard,
  PNRGuard,
  SinglePaxGuard,
  OneUpgradeGuard,
  ProcessingGuard,
  RefetchGuard,
  BasketGuard,
  SuccessGuard,
} from './guards/index';
import { StatusBarComponent } from './components/status-bar/status-bar.component';
import { ContinueFooterComponent } from './components/continue-footer/continue-footer.component';
import { UpgradeModalComponent } from './components/modal/modal.component';
import { UPGRADE_FEATURE_KEY } from './constants';
import { LoadingComponent } from './views/loading/loading.component';
import { PassengersSelectionComponent } from './views/passengers-selection/passengers-selection.component';
import { FlightsSelectionComponent } from './views/flights-selection/flights-selection.component';

@NgModule({
  imports: [
    OneUpgradeRouteModule,
    CommonModule,
    FormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    UiComponentsModule,
    ComponentsModule,
    DirectiveModule,
    GtmModule,
    PipesModule,
    StoreModule.forFeature(UPGRADE_FEATURE_KEY, reducers),
    EffectsModule.forFeature([AuthEffects, StatusEffects, UpgradeFlightsEffects]),
    UiTranslateModule,
    ApiModule,
    LoginModule,
    NavigationModule,
    BookingCommonModule,
    LocationsModule,
    ImagesModule,
    CmsModule,
    QualtricsModule,
    CommonBookingComponentsModule,
    PnrWidgetModule,
  ],
  providers: [
    LoginGuard,
    PNRGuard,
    SinglePaxGuard,
    OneUpgradeGuard,
    ProcessingGuard,
    RefetchGuard,
    BasketGuard,
    SuccessGuard,
    CmsPageService,
    FragmentService,
  ],
  declarations: [
    LoginComponent,
    LoadingComponent,
    PassengersSelectionComponent,
    FlightsSelectionComponent,
    ReviewComponent,
    ErrorComponent,
    SuccessComponent,
    ProcessingComponent,
    BoundCardComponent,
    JourneyInfoComponent,
    ReviewBoundsComponent,
    UpgradeBoundsComponent,
    UpgradeFlightsComponent,
    PassengerSelectionComponent,
    OneUpgradeLayoutComponent,
    StatusBarComponent,
    ContinueFooterComponent,
    UpgradeModalComponent,
  ],
})
export class OneUpgradeModule {
  constructor(iconsPreloadService: IconsPreloadService) {
    iconsPreloadService.preloadIcons([
      { category: IconLibrary.SVG_LIBRARY, name: SvgLibraryIcon.CHECKBOX_SELECTED },
      { category: IconLibrary.SVG_LIBRARY, name: SvgLibraryIcon.CHECKBOX_UNSELECTED },
      { category: IconLibrary.SVG_LIBRARY, name: SvgLibraryIcon.CHECKMARK },
    ]);
  }
}
